import { Box, Button, IconFont, Text, Toast, Tooltip } from '@byecode/ui'
import {
    disableWorkflowErrorMessage,
    enableWorkflowErrorMessage,
    type FlowEdge,
    type FlowNode,
    updateWorkflowErrorMessage,
    useAtomAction,
    useAtomData,
    WorkflowType
} from '@lighthouse/shared'
import { Switch } from '@mantine/core'
import { Panel, useReactFlow } from '@xyflow/react'
import { useCallback } from 'react'
import styled, { css } from 'styled-components'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { workflowAtomFamily } from '@/atoms/workflow/state'
import { FlowDetailShape, useFlowContainerContext } from '@/contexts/FlowContext'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useWorkSpace } from '@/hooks/useWorkSpace'
import * as srv from '@/services'

import { usePersistFlow } from '../../hook/usePersistFlow'
import { validateNode } from '../../utils/nodeValidator'
import { resolveWebhookNodes } from '../../utils/webhookUrl'
import { FlowLeave } from './FlowLeave'

const SCPanel = styled(Panel)`
    display: flex;
    gap: 8px;
    align-items: center;
`

const SCRunActionGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px;
    border-radius: 6px;
    background-color: var(--color-white);
    box-shadow: 0px 1px 3px 0px #1018281a;
`

const SCButtonGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const StyledButton = styled(Button)<{ active?: boolean }>`
    border: none;

    ${({ active }) =>
        active
            ? css`
                  color: var(--color-main);
                  background-color: var(--color-purple-100);
                  &:hover {
                      color: var(--color-main) !important;
                      background-color: var(--color-purple-200) !important;
                  }
              `
            : css`
                  color: var(--color-gray-400);
                  &:hover {
                      color: var(--color-main) !important;
                      background-color: var(--color-purple-100) !important;
                  }
              `}
`

export const RightActionPanel = () => {
    const {
        log,
        id,
        isChanged,
        enabled,
        shape,
        runType,
        setIsChanged,
        modalState,
        setModalState,
        setErrorMap,
        onFlowChange,
        requestClose,
        type
    } = useFlowContainerContext()
    const readonly = !!log
    const disabledWithVersion = useIsDisabledWithVersion()
    const { run: updateWorkflow } = useAtomAction(workflowAtomFamily(id))
    const { persistFlow } = usePersistFlow()

    const { getNodes, getEdges } = useReactFlow()
    const accounts = useAtomData(otherPlatformListAtom)
    const { apiKey } = useWorkSpace()

    const handleFlowEnabledChange = useCallback(
        async (enabled: boolean) => {
            const nodes = resolveWebhookNodes(getNodes() as FlowNode[], apiKey)
            const edges = getEdges() as FlowEdge[]

            if (enabled) {
                const isError = nodes.some(node => !validateNode(node, accounts))
                if (isError) {
                    Toast.error(enableWorkflowErrorMessage)
                    return
                }

                try {
                    const res = await srv.updateAndEnableWorkflow({ id, nodes, edges, runType })
                    if (res.success) {
                        Toast.success('启用成功')
                        setIsChanged?.(false)
                        updateWorkflow(draft => {
                            if (!draft) {
                                return
                            }
                            draft.enabled = enabled
                            draft.content.nodes = nodes
                            draft.content.edges = edges
                        })
                        return
                    }
                    setErrorMap?.(res.errorMap)
                    Toast.error(updateWorkflowErrorMessage)
                } catch {
                    Toast.error(updateWorkflowErrorMessage)
                }
                return
            }
            try {
                const isEnable = await srv.enableFlow(id, enabled)
                if (!isEnable) {
                    Toast.error(enabled ? enableWorkflowErrorMessage : disableWorkflowErrorMessage)
                    return
                }
                Toast.success(`${enabled ? '启用' : '禁用'}成功`)
                updateWorkflow(draft => {
                    if (!draft) {
                        return
                    }
                    draft.enabled = enabled
                    draft.content.nodes = nodes
                    draft.content.edges = edges
                })
            } catch {
                Toast.error(disableWorkflowErrorMessage)
            }
        },
        [accounts, apiKey, getEdges, getNodes, id, runType, setErrorMap, setIsChanged, updateWorkflow]
    )

    const handleBackEditor = useCallback(() => {
        onFlowChange?.({
            mode: 'editor'
        })
    }, [onFlowChange])

    const onClickShowFlowResult = useCallback(() => {
        setModalState({ type: modalState.type === 'FLOW_RUNNING_RESULT' ? undefined : 'FLOW_RUNNING_RESULT' })
    }, [modalState.type, setModalState])

    const onClickShowRunHistory = useCallback(() => {
        setModalState({ type: modalState.type === 'FLOW_RUNNING_HISTORY' ? undefined : 'FLOW_RUNNING_HISTORY' })
    }, [modalState, setModalState])

    const onClickShowFlowStartTest = useCallback(() => {
        setModalState({ type: modalState.type === 'FLOW_RUNNING_START_TEST' ? undefined : 'FLOW_RUNNING_START_TEST' })
    }, [modalState.type, setModalState])

    const onClickShowFlowConfigure = useCallback(() => {
        setModalState({ type: modalState.type === 'FLOW_CONFIGURE' ? undefined : 'FLOW_CONFIGURE' })
    }, [modalState.type, setModalState])

    return (
        <SCPanel position="top-right">
            {readonly ? (
                <>
                    <SCRunActionGroup>
                        <StyledButton
                            active={modalState.type === 'FLOW_RUNNING_RESULT'}
                            radius={4}
                            icon={<IconFont size={16} type="LinearDocument" />}
                            onClick={onClickShowFlowResult}
                        >
                            <Text color={modalState.type === 'FLOW_RUNNING_RESULT' ? 'var(--color-main)' : 'var(--color-black)'}>
                                运行结果
                            </Text>
                        </StyledButton>
                    </SCRunActionGroup>
                    {shape !== FlowDetailShape.LOG_MODAL && (
                        <>
                            <SCRunActionGroup>
                                <StyledButton
                                    active={modalState.type === 'FLOW_RUNNING_HISTORY'}
                                    radius={4}
                                    icon={<IconFont size={16} type="PlayHistory" />}
                                    onClick={onClickShowRunHistory}
                                >
                                    <Text color={modalState.type === 'FLOW_RUNNING_HISTORY' ? 'var(--color-main)' : 'var(--color-black)'}>
                                        运行记录
                                    </Text>
                                </StyledButton>
                            </SCRunActionGroup>
                            <Box p={2}>
                                <Button type="primary" onClick={handleBackEditor} radius={4} icon={<IconFont size={16} type="ArrowLeft" />}>
                                    返回编辑
                                </Button>
                            </Box>
                        </>
                    )}
                </>
            ) : (
                <>
                    <SCRunActionGroup>
                        {/* <StyledButton
                            active={modalState.type === 'FLOW_RUNNING_START_TEST'}
                            radius={4}
                            icon={<IconFont size={16} type="LinearPlay" />}
                            onClick={onClickShowFlowStartTest}
                        >
                            <Text color={modalState.type === 'FLOW_RUNNING_START_TEST' ? 'var(--color-main)' : 'var(--color-black)'}>
                                测试运行
                            </Text>
                        </StyledButton>
                        <Divider style={{ height: 16, alignSelf: 'center' }} direction="vertical" /> */}
                        <Tooltip title="运行记录">
                            <StyledButton
                                active={modalState.type === 'FLOW_RUNNING_HISTORY'}
                                radius={4}
                                icon={<IconFont size={16} type="PlayHistory" />}
                                onClick={onClickShowRunHistory}
                            />
                        </Tooltip>
                        {type !== WorkflowType.SUB_PROCESS && (
                            <StyledButton
                                active={modalState.type === 'FLOW_CONFIGURE'}
                                icon={<IconFont size={16} type="Setting" />}
                                onClick={onClickShowFlowConfigure}
                            />
                        )}
                    </SCRunActionGroup>
                    <SCButtonGroup>
                        <Switch
                            disabled={disabledWithVersion}
                            color="var(--color-main)"
                            radius={6}
                            size="xl"
                            styles={{
                                track: {
                                    fontSize: 14
                                },
                                trackLabel: { minWidth: 48, fontWeight: 'normal' },
                                thumb: {
                                    borderRadius: 4
                                }
                            }}
                            thumbIcon={
                                <IconFont
                                    type="Tick"
                                    style={{ fontSize: 16, color: enabled ? 'var(--color-main)' : 'var(--color-gray-500)' }}
                                />
                            }
                            onLabel="已启用"
                            offLabel="未启用"
                            checked={enabled}
                            onChange={e => handleFlowEnabledChange?.(e.target.checked)}
                        />
                        <Button disabled={!isChanged || disabledWithVersion} type="primary" size="lg" onClick={persistFlow}>
                            {enabled ? '更新' : '保存'}
                        </Button>
                    </SCButtonGroup>
                </>
            )}
            <FlowLeave />
            {shape !== FlowDetailShape.PAGE && (
                <Button
                    style={{ width: 28, height: 28, padding: 4, borderRadius: 28 }}
                    size="sm"
                    type="default"
                    icon={<IconFont size={16} fill="var(--color-gray-400)" type="Close" />}
                    onClick={requestClose}
                />
            )}
        </SCPanel>
    )
}
